import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ArrowLeft, Award, TrendingUp, Target, Star,
  RefreshCw, CheckCircle, AlertTriangle, Zap,
  BookOpen, MessageSquare, Heart, Brain, Trophy,
  ChevronRight, BarChart3, Calendar
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ObserverNav from '../components/ObserverNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverCoaching = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [coaching, setCoaching] = useState(null);
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('observer_token');
    if (!token) {
      navigate('/observer/login');
      return;
    }
    loadCoachingData();
  }, [navigate]);

  const loadCoachingData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('observer_token');
      
      // Load call analysis for coaching tips
      const analysisRes = await axios.get(
        `${BACKEND_URL}/api/analysis/observer/call-analysis?token=${token}`
      );
      
      // Load observer performance data
      const perfRes = await axios.get(
        `${BACKEND_URL}/api/observer/performance?token=${token}`
      ).catch(() => ({ data: {} }));
      
      // Aggregate coaching insights
      const coachingData = {
        has_coaching: analysisRes.data?.has_analysis,
        performance: perfRes.data,
        tips: analysisRes.data?.coaching_tips || [],
        scores: analysisRes.data?.performance_scores || {},
        what_worked: analysisRes.data?.what_worked || [],
        learning_points: analysisRes.data?.learning_points || []
      };
      
      setCoaching(coachingData);
    } catch (error) {
      console.error('Error loading coaching data:', error);
      toast({ title: "Error", description: "Failed to load coaching data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'from-green-400 to-emerald-500';
    if (score >= 5) return 'from-amber-400 to-orange-500';
    return 'from-red-400 to-rose-500';
  };

  const getGrade = (score) => {
    if (score >= 9) return 'A+';
    if (score >= 8) return 'A';
    if (score >= 7) return 'B+';
    if (score >= 6) return 'B';
    if (score >= 5) return 'C+';
    if (score >= 4) return 'C';
    return 'Needs Work';
  };

  // Calculate average score
  const avgScore = coaching?.scores ? 
    Object.values(coaching.scores).reduce((a, b) => a + b, 0) / Object.keys(coaching.scores).length : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ObserverNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="observer-coaching">
      <ObserverNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/observer/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Award className="w-6 h-6 text-violet-600" />
                  AI Coaching Center
                </h1>
                <p className="text-gray-600">Personalized feedback to help you grow</p>
              </div>
            </div>
            <Button variant="outline" onClick={loadCoachingData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* No Coaching State */}
          {!coaching?.has_coaching && (
            <Card className="text-center py-12">
              <CardContent>
                <Award className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No Coaching Data Yet</h3>
                <p className="text-gray-500 mb-4">Complete some sessions to receive personalized AI coaching</p>
                <Button onClick={() => navigate('/observer/sessions')}>
                  Start a Session
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Coaching Content */}
          {coaching?.has_coaching && (
            <>
              {/* Overall Score Card */}
              <Card className={`mb-6 bg-gradient-to-r ${getScoreColor(avgScore)} text-white border-0`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/80 text-sm mb-1">Your Overall Performance</p>
                      <div className="flex items-baseline gap-3">
                        <p className="text-5xl font-bold">{avgScore.toFixed(1)}</p>
                        <p className="text-2xl text-white/80">/10</p>
                      </div>
                      <p className="text-white/70 mt-2">Grade: {getGrade(avgScore)}</p>
                    </div>
                    <div className="text-right">
                      <Trophy className="w-16 h-16 text-white/30" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Score Breakdown */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                    Score Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(coaching.scores).map(([key, value]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600 capitalize font-medium">
                            {key.replace(/_/g, ' ')}
                          </span>
                          <span className={`text-sm font-bold ${
                            value >= 7 ? 'text-green-600' : value >= 5 ? 'text-amber-600' : 'text-red-600'
                          }`}>
                            {value}/10
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full transition-all duration-500 ${
                              value >= 7 ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                              value >= 5 ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                              'bg-gradient-to-r from-red-400 to-rose-500'
                            }`}
                            style={{ width: `${(value / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Strengths & Areas to Improve */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Strengths */}
                <Card className="border-green-200">
                  <CardHeader className="pb-3 bg-green-50">
                    <CardTitle className="flex items-center gap-2 text-green-700">
                      <Star className="w-5 h-5" />
                      Your Strengths
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {coaching.what_worked?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{item}</p>
                        </div>
                      ))}
                      {coaching.what_worked?.length === 0 && (
                        <p className="text-gray-500 text-sm">Keep working on building your strengths!</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Areas to Improve */}
                <Card className="border-amber-200">
                  <CardHeader className="pb-3 bg-amber-50">
                    <CardTitle className="flex items-center gap-2 text-amber-700">
                      <Target className="w-5 h-5" />
                      Areas to Grow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-3">
                      {coaching.learning_points?.map((item, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700 text-sm">{item}</p>
                        </div>
                      ))}
                      {coaching.learning_points?.length === 0 && (
                        <p className="text-gray-500 text-sm">Great job! Keep up the good work!</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* AI Coaching Tips */}
              <Card className="mb-6">
                <CardHeader className="bg-gradient-to-r from-violet-50 to-purple-50">
                  <CardTitle className="flex items-center gap-2 text-violet-700">
                    <Zap className="w-5 h-5" />
                    Personalized Coaching Tips
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {coaching.tips?.map((tip, idx) => (
                      <div 
                        key={idx} 
                        className="flex items-start gap-4 p-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg border border-violet-100"
                      >
                        <div className="w-10 h-10 bg-violet-500 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">{idx + 1}</span>
                        </div>
                        <div>
                          <p className="text-gray-800">{tip}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Focus */}
              <Card className="mb-6 bg-gradient-to-br from-blue-500 to-violet-500 text-white border-0">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <Calendar className="w-8 h-8 text-white/80" />
                    <div>
                      <h3 className="text-lg font-bold">This Week's Focus</h3>
                      <p className="text-white/70 text-sm">Based on your recent performance</p>
                    </div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="text-white">
                      {coaching.learning_points?.[0] || 
                       "Continue building rapport through consistent sessions and open-ended questions."}
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <div className="grid md:grid-cols-3 gap-4">
                <Button 
                  onClick={() => navigate('/observer/call-analysis')}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <Brain className="w-6 h-6 text-purple-600" />
                  <span>Call Analysis</span>
                </Button>
                <Button 
                  onClick={() => navigate('/observer/prep-notes')}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <BookOpen className="w-6 h-6 text-blue-600" />
                  <span>Prep Notes</span>
                </Button>
                <Button 
                  onClick={() => navigate('/observer/quality-metrics')}
                  variant="outline"
                  className="h-auto py-4 flex-col gap-2"
                >
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <span>Quality Metrics</span>
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverCoaching;
